import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { getUserProfile, updateUserProfile, getFighterTags, fetchFighterTagsForUser } from '../../services/supabaseService';
import { PREDEFINED_AVATARS, PREDEFINED_COVERS } from '../../types';
import { checkUsernameForPhishing } from '../../lib/validation';
import { supabase } from '../../lib/supabase';

export const EditProfile: React.FC = () => {
  const { user: currentUser, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const [dbGames, setDbGames] = useState<{ id: any; name: string }[]>([]);
  const [allFighterTags, setAllFighterTags] = useState<{ id: any; name: string }[]>([]);
  const [showCustomAvatarInput, setShowCustomAvatarInput] = useState(false);
  const [showCustomBannerInput, setShowCustomBannerInput] = useState(false);

  const [formData, setFormData] = useState({
    fullname: '',
    username: '',
    bio: '',
    phone: '',
    birth_date: '',
    gender: '',
    fav_game_id: '',
    avatar: '',
    banner_url: '',
    bandai_member: '',
    fighter_tags: [] as string[]
  });

  // Calculate maximum birth date (minimum 10 years old according to DB chk_minimum_age constraint)
  const maxBirthDate = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 10);
    return d.toISOString().split('T')[0];
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const { getCardgames } = await import('../../services/supabaseService');
        const [games, tags] = await Promise.all([
          getCardgames(),
          getFighterTags()
        ]);
        setDbGames(games || []);
        setAllFighterTags(tags || []);
      } catch (err) {
        console.error('Erro ao buscar dados iniciais:', err);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      if (currentUser) {
        setLoading(true);
        try {
          // Busca dados completos do usuário diretamente da tabela users para ter todas as colunas
          let profile: any = null;
          if (currentUser.auth_id) {
            const { data } = await supabase
              .from('users')
              .select('*')
              .eq('auth_id', currentUser.auth_id)
              .maybeSingle();
            profile = data;
          }
          if (!profile && currentUser.id) {
            const { data } = await supabase
              .from('users')
              .select('*')
              .eq('id', currentUser.id)
              .maybeSingle();
            profile = data;
          }
          if (!profile) {
            profile = await getUserProfile(currentUser.id || currentUser.email_copy || currentUser.username);
          }

          if (profile) {
            let tags: string[] = [];
            if (Array.isArray(profile.fighter_tags)) {
              tags = profile.fighter_tags;
            } else if (typeof profile.fighter_tags === 'string') {
              tags = profile.fighter_tags.split(',').map((t: string) => t.trim()).filter((t: string) => t !== '');
            } else if (profile.id) {
              const fetchedTags = await fetchFighterTagsForUser(profile.id);
              tags = fetchedTags || [];
            }

            let birthDate = profile.birth_date || '';
            if (birthDate && birthDate.includes('T')) {
              birthDate = birthDate.split('T')[0];
            }

            const initialAvatar = profile.avatar || profile.photoURL || currentUser.avatar || PREDEFINED_AVATARS[0];
            const initialBanner = profile.banner_url || profile.cover_url || currentUser.banner_url || PREDEFINED_COVERS[0];

            if (initialAvatar && !PREDEFINED_AVATARS.includes(initialAvatar)) {
              setShowCustomAvatarInput(true);
            }
            if (initialBanner && !PREDEFINED_COVERS.includes(initialBanner)) {
              setShowCustomBannerInput(true);
            }

            setFormData({
              fullname: profile.fullname || currentUser.fullname || '',
              username: profile.username || currentUser.username || '',
              bio: profile.bio || '',
              phone: profile.phone || '',
              birth_date: birthDate,
              gender: profile.gender || '',
              fav_game_id: profile.fav_game_id ? String(profile.fav_game_id) : (profile.favorite_cardgame_id ? String(profile.favorite_cardgame_id) : ''),
              avatar: initialAvatar,
              banner_url: initialBanner,
              bandai_member: profile.bandai_member || currentUser.bandai_member || '',
              fighter_tags: tags
            });
          }
        } catch (err) {
          console.error('Erro ao carregar perfil:', err);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchProfile();
  }, [currentUser]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTagToggle = (tag: string) => {
    setFormData(prev => {
      if (prev.fighter_tags.includes(tag)) {
        return { ...prev, fighter_tags: prev.fighter_tags.filter(t => t !== tag) };
      }
      return { ...prev, fighter_tags: [...prev.fighter_tags, tag] };
    });
  };

  const handleAvatarSelect = (avatar: string) => {
    setFormData(prev => ({ ...prev, avatar }));
  };

  const handleCoverSelect = (banner_url: string) => {
    setFormData(prev => ({ ...prev, banner_url }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    // 1. Validação de Nome Completo (obrigatório no schema: fullname character varying not null)
    if (!formData.fullname || !formData.fullname.trim()) {
      setMessage({ type: 'error', text: 'O campo Nome Completo é obrigatório.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // 2. Validação de Username (obrigatório no schema: username character varying not null)
    if (!formData.username || !formData.username.trim()) {
      setMessage({ type: 'error', text: 'Nome de usuário não pode ficar vazio.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const usernameCheck = checkUsernameForPhishing(formData.username);
    if (!usernameCheck.isValid) {
      setMessage({ type: 'error', text: usernameCheck.error || 'Nome de usuário inválido.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // 3. Validação de Idade Mínima (constraint chk_minimum_age: birth_date <= CURRENT_DATE - 10 years)
    if (formData.birth_date && formData.birth_date > maxBirthDate) {
      setMessage({ type: 'error', text: 'A data de nascimento deve corresponder a no mínimo 10 anos de idade.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // 4. Validação de Telefone (máximo 20 caracteres no schema: phone character varying(20))
    if (formData.phone && formData.phone.length > 20) {
      setMessage({ type: 'error', text: 'O telefone deve conter no máximo 20 caracteres.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const targetIdentifier = currentUser.auth_id || currentUser.id || currentUser.email_copy || currentUser.email;
      const result: any = await updateUserProfile(targetIdentifier, {
        fullname: formData.fullname,
        username: formData.username,
        bio: formData.bio,
        phone: formData.phone,
        birth_date: formData.birth_date,
        gender: formData.gender,
        fav_game_id: formData.fav_game_id,
        avatar: formData.avatar,
        banner_url: formData.banner_url,
        bandai_member: formData.bandai_member,
        fighter_tags: formData.fighter_tags
      });

      if (result && result.success !== false) {
        if (refreshProfile) {
          await refreshProfile();
        }
        setMessage({ type: 'success', text: 'Perfil atualizado com sucesso!' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const errorMsg = result?.error || 'Falha ao atualizar perfil. Verifique os dados e tente novamente.';
        setMessage({ type: 'error', text: errorMsg });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error: any) {
      console.error('Erro ao salvar perfil:', error);
      setMessage({ type: 'error', text: error?.message || 'Erro ao conectar ao servidor.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
        <p className="text-slate-400 text-xs font-bold uppercase tracking-widest">Carregando dados do perfil...</p>
      </div>
    );
  }

  const accountEmail = currentUser?.email_copy || currentUser?.email || 'Não informado';

  return (
    <div className="max-w-4xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto pb-20 pt-6 animate-in fade-in slide-in-from-bottom-4 duration-500 px-4 md:px-0">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black text-white uppercase tracking-tighter">Editar Perfil</h1>
          <p className="text-slate-400 text-sm">Personalize sua identidade e informações no ecossistema Cardumy</p>
        </div>
        <div className="flex items-center space-x-2">
          <button 
            type="button"
            onClick={() => navigate('/configuracoes')}
            className="text-slate-400 hover:text-white transition-colors p-2 rounded-xl hover:bg-slate-800 text-xs font-bold flex items-center space-x-1.5"
            title="Configurações do Site"
          >
            <i className="fas fa-sliders"></i>
            <span className="hidden sm:inline">Configurações</span>
          </button>
          <button 
            type="button"
            onClick={() => navigate('/perfil')}
            className="text-slate-400 hover:text-white transition-colors p-2 rounded-xl hover:bg-slate-800"
            title="Voltar ao Perfil"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-2xl flex items-center space-x-3 transition-all ${message.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
          <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'} text-lg shrink-0`}></i>
          <span className="text-sm font-bold">{message.text}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        
        {/* Banner do Perfil */}
        <section className="bg-slate-900/30 border border-slate-800 p-6 md:p-8 rounded-[32px] space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold flex items-center space-x-2 text-white">
              <i className="fas fa-panorama text-purple-500"></i>
              <span>Capa do Perfil (Banner)</span>
            </h3>
            <button
              type="button"
              onClick={() => setShowCustomBannerInput(prev => !prev)}
              className="text-xs text-purple-400 hover:text-purple-300 font-bold flex items-center gap-1.5 transition-colors"
            >
              <i className={`fas ${showCustomBannerInput ? 'fa-list' : 'fa-link'}`}></i>
              <span>{showCustomBannerInput ? 'Escolher da Galeria' : 'Usar URL personalizada'}</span>
            </button>
          </div>

          {/* Preview da Capa Selecionada */}
          {formData.banner_url && (
            <div className="relative h-28 sm:h-36 md:h-44 w-full rounded-2xl overflow-hidden border border-slate-800 shadow-inner group">
              <img 
                src={formData.banner_url} 
                alt="Prévia da capa" 
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = PREDEFINED_COVERS[0];
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent flex items-end p-3">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-300 bg-slate-900/80 px-2.5 py-1 rounded-lg backdrop-blur-sm border border-white/10">
                  Prévia da Capa
                </span>
              </div>
            </div>
          )}

          {showCustomBannerInput ? (
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">URL da Imagem da Capa</label>
              <div className="relative">
                <span className="absolute left-4 top-3.5 text-slate-500 text-sm">
                  <i className="fas fa-link"></i>
                </span>
                <input
                  type="url"
                  name="banner_url"
                  value={formData.banner_url}
                  onChange={handleChange}
                  placeholder="https://exemplo.com/minha-capa.jpg"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 py-3 text-sm text-white focus:border-purple-500 transition-colors"
                />
              </div>
              <p className="text-[11px] text-slate-500 ml-1">Cole o link direto para uma imagem de alta resolução (proporção panorâmica recomendada).</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {PREDEFINED_COVERS.map((cover, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleCoverSelect(cover)}
                  className={`relative h-24 md:h-32 rounded-2xl overflow-hidden border-4 transition-all ${formData.banner_url === cover ? 'border-purple-500 scale-105 shadow-lg shadow-purple-500/20' : 'border-slate-800 hover:border-slate-700'}`}
                >
                  <img src={cover} alt={`Capa ${i}`} className="w-full h-full object-cover" />
                  {formData.banner_url === cover && (
                    <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                      <i className="fas fa-check text-white shadow-sm"></i>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Avatar do Usuário */}
        <section className="bg-slate-900/30 border border-slate-800 p-6 md:p-8 rounded-[32px] space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold flex items-center space-x-2 text-white">
              <i className="fas fa-image text-purple-500"></i>
              <span>Foto de Perfil (Avatar)</span>
            </h3>
            <button
              type="button"
              onClick={() => setShowCustomAvatarInput(prev => !prev)}
              className="text-xs text-purple-400 hover:text-purple-300 font-bold flex items-center gap-1.5 transition-colors"
            >
              <i className={`fas ${showCustomAvatarInput ? 'fa-list' : 'fa-link'}`}></i>
              <span>{showCustomAvatarInput ? 'Escolher da Galeria' : 'Usar URL personalizada'}</span>
            </button>
          </div>

          {/* Preview do Avatar */}
          <div className="flex items-center gap-4 p-3 bg-slate-950/60 rounded-2xl border border-slate-800/80">
            <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden border-2 border-purple-500 shadow-md shrink-0">
              <img 
                src={formData.avatar || PREDEFINED_AVATARS[0]} 
                alt="Prévia do Avatar" 
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = PREDEFINED_AVATARS[0];
                }}
              />
            </div>
            <div>
              <p className="text-sm font-bold text-white">Avatar Atual</p>
              <p className="text-xs text-slate-400">Este ícone representará você nas partidas, ofertas de troca e comunidade.</p>
            </div>
          </div>

          {showCustomAvatarInput ? (
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">URL da Imagem do Avatar</label>
              <div className="relative">
                <span className="absolute left-4 top-3.5 text-slate-500 text-sm">
                  <i className="fas fa-link"></i>
                </span>
                <input
                  type="url"
                  name="avatar"
                  value={formData.avatar}
                  onChange={handleChange}
                  placeholder="https://exemplo.com/meu-avatar.png"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 py-3 text-sm text-white focus:border-purple-500 transition-colors"
                />
              </div>
              <p className="text-[11px] text-slate-500 ml-1">Insira o link direto para a imagem desejada (formato quadrado recomendado).</p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-4 max-h-72 overflow-y-auto pr-1">
              {PREDEFINED_AVATARS.map((avatar, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleAvatarSelect(avatar)}
                  className={`relative w-14 h-14 md:w-16 md:h-16 rounded-full overflow-hidden border-2 transition-all ${formData.avatar === avatar ? 'border-purple-500 scale-110 shadow-lg shadow-purple-500/20' : 'border-slate-800 hover:border-slate-700'}`}
                >
                  <img src={avatar} alt={`Avatar ${i}`} className="w-full h-full object-cover" />
                  {formData.avatar === avatar && (
                    <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                      <i className="fas fa-check text-white text-xs shadow-sm"></i>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Informações Básicas e Pessoais */}
        <section className="bg-slate-900/30 border border-slate-800 p-6 md:p-8 rounded-[32px] space-y-6">
          <h3 className="text-lg font-bold flex items-center space-x-2 text-white">
            <i className="fas fa-id-card text-purple-500"></i>
            <span>Informações Pessoais & Conta</span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Nome Completo (fullname) */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 flex items-center gap-1">
                <span>Nome Completo</span>
                <span className="text-red-400">*</span>
              </label>
              <input 
                type="text" 
                name="fullname"
                required
                value={formData.fullname || ''}
                onChange={handleChange}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white focus:border-purple-500 transition-colors"
                placeholder="Seu nome completo"
              />
            </div>

            {/* Username */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 flex items-center gap-1">
                <span>Nome de Usuário (@username)</span>
                <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-3.5 text-slate-500 text-sm">@</span>
                <input 
                  type="text" 
                  name="username"
                  required
                  value={formData.username || ''}
                  onChange={handleChange}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-8 pr-4 py-3 text-sm text-white focus:border-purple-500 transition-colors"
                  placeholder="seu_username"
                />
              </div>
            </div>

            {/* E-mail (Somente leitura para integridade de autenticação) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between ml-1">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">E-mail da Conta</label>
                <span className="text-[10px] font-bold text-slate-500 flex items-center gap-1">
                  <i className="fas fa-lock text-[9px]"></i>
                  <span>Autenticação</span>
                </span>
              </div>
              <input 
                type="text" 
                readOnly
                disabled
                value={accountEmail}
                className="w-full bg-slate-950/60 border border-slate-800/80 rounded-xl px-4 py-3 text-sm text-slate-400 cursor-not-allowed select-none"
              />
            </div>

            {/* WhatsApp / Telefone (phone) */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">WhatsApp / Telefone</label>
              <div className="relative">
                <span className="absolute left-4 top-3.5 text-slate-500 text-sm">
                  <i className="fas fa-phone"></i>
                </span>
                <input 
                  type="tel" 
                  name="phone"
                  maxLength={20}
                  value={formData.phone || ''}
                  onChange={handleChange}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 py-3 text-sm text-white focus:border-purple-500 transition-colors"
                  placeholder="(11) 99999-9999"
                />
              </div>
              <p className="text-[10px] text-slate-500 ml-1">Essencial para ser contatado em negociações e trocas de cartas.</p>
            </div>

            {/* Data de Nascimento (birth_date) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between ml-1">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Data de Nascimento</label>
                <span className="text-[10px] text-purple-400 font-semibold">Mínimo de 10 anos</span>
              </div>
              <input 
                type="date" 
                name="birth_date"
                max={maxBirthDate}
                value={formData.birth_date || ''}
                onChange={handleChange}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white focus:border-purple-500 transition-colors [color-scheme:dark]"
              />
            </div>

            {/* Gênero (gender) */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Gênero</label>
              <select 
                name="gender"
                value={formData.gender || ''}
                onChange={handleChange}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white focus:border-purple-500 transition-colors"
              >
                <option value="">Não informado / Selecionar</option>
                <option value="Masculino">Masculino</option>
                <option value="Feminino">Feminino</option>
                <option value="Não-binário">Não-binário</option>
                <option value="Outro">Outro</option>
                <option value="Prefiro não dizer">Prefiro não dizer</option>
              </select>
            </div>

            {/* Bandai Member (TCG+ ID) - Privado */}
            <div className="space-y-2">
              <div className="flex items-center justify-between ml-1">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                  <i className="fas fa-id-badge text-purple-400"></i>
                  <span>Bandai Member (TCG+ ID)</span>
                </label>
                <span className="text-[10px] font-bold text-slate-500 flex items-center gap-1">
                  <i className="fas fa-lock text-[9px]"></i>
                  <span>Privado</span>
                </span>
              </div>
              <div className="relative">
                <span className="absolute left-4 top-3.5 text-slate-500 text-sm">
                  <i className="fas fa-barcode"></i>
                </span>
                <input 
                  type="text" 
                  name="bandai_member"
                  value={formData.bandai_member || ''}
                  onChange={handleChange}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 py-3 text-sm text-white focus:border-purple-500 transition-colors font-mono"
                  placeholder="Ex: 0001234567"
                />
              </div>
              <p className="text-[10px] text-slate-500 ml-1">Informação privada (não exibida publicamente). Usada para identificação e torneios Bandai.</p>
            </div>

            {/* Jogo Favorito (fav_game_id) */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">TCG / Jogo Favorito</label>
              <select 
                name="fav_game_id"
                value={formData.fav_game_id || ''}
                onChange={handleChange}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white focus:border-purple-500 transition-colors"
              >
                <option value="">Selecione seu TCG principal</option>
                {dbGames.map(game => (
                  <option key={game.id} value={game.id}>{game.name}</option>
                ))}
              </select>
              <p className="text-[10px] text-slate-500 ml-1">Seu jogo de cartas principal no ecossistema Cardumy.</p>
            </div>

            {/* Bio / Sobre Você (bio) */}
            <div className="space-y-2 md:col-span-2">
              <div className="flex items-center justify-between ml-1">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Bio / Sobre Você</label>
                <span className="text-[10px] text-slate-500 font-bold">{formData.bio?.length || 0} caracteres</span>
              </div>
              <textarea 
                name="bio"
                value={formData.bio || ''}
                onChange={handleChange}
                rows={4}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white focus:border-purple-500 transition-colors resize-none leading-relaxed"
                placeholder="Conte um pouco sobre sua trajetória no mundo dos cardgames, suas coleções, decks favoritos ou estilo de jogo..."
              />
            </div>
          </div>
        </section>

        {/* Fighter Tags */}
        <section className="bg-slate-900/30 border border-slate-800 p-6 md:p-8 rounded-[32px] space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold flex items-center space-x-2 text-white">
              <i className="fas fa-tags text-purple-500"></i>
              <span>Fighter Tags</span>
            </h3>
            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{formData.fighter_tags.length} selecionadas</span>
          </div>
          <p className="text-xs text-slate-400">Escolha as tags que melhor descrevem seu perfil e objetivos como jogador e colecionador:</p>
          <div className="flex flex-wrap gap-2">
            {allFighterTags.map(tag => (
              <button
                key={tag.id}
                type="button"
                onClick={() => handleTagToggle(tag.name)}
                className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-wider border transition-all ${formData.fighter_tags.includes(tag.name) ? 'bg-purple-600 border-purple-500 text-white shadow-lg shadow-purple-600/20 scale-105' : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'}`}
              >
                {tag.name}
              </button>
            ))}
            {allFighterTags.length === 0 && (
              <p className="text-[10px] text-slate-600 italic">Carregando tags disponíveis...</p>
            )}
          </div>
        </section>

        {/* Botões de Ação */}
        <div className="flex flex-col sm:flex-row items-center gap-4 pt-4 pb-12">
          <button 
            type="submit"
            disabled={saving}
            className="w-full sm:w-auto bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-black px-12 py-4 rounded-2xl transition-all shadow-xl shadow-purple-600/20 active:scale-95 text-sm uppercase tracking-widest flex items-center justify-center gap-3"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Salvando...</span>
              </>
            ) : (
              <>
                <i className="fas fa-floppy-disk"></i>
                <span>Salvar Alterações</span>
              </>
            )}
          </button>
          <button 
            type="button"
            onClick={() => navigate('/perfil')}
            className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white font-black px-10 py-4 rounded-2xl transition-all active:scale-95 text-sm uppercase tracking-widest border border-slate-800"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
};
